import React, { forwardRef, useId } from 'react';
import { focusRing } from './tokens';

// Accessible text input + textarea + select primitives. Each renders a
// visible <label>, optional description / error, and wires aria-describedby
// + aria-invalid so screen readers announce errors automatically.

const baseField =
  'w-full rounded-lg border bg-white px-3 py-2 text-sm text.heading placeholder:text.subtle transition-colors motion-reduce:transition-none disabled:bg-gray-50 disabled:text.subtle disabled:cursor-not-allowed';

const ringClass = 'border-gray-300 focus:border-purple-500 ' + focusRing;
const errorClass = 'border-red-400 focus:border-red-500 focus-visible:ring-red-500';

const Field = ({ id, label, hint, error, required, wrapperClassName = '', children }) => {
  // Skip the wrapper entirely when there's no label/hint/error — caller
  // can drop the input into a flex/grid without an extra layout div.
  const hasMeta = label || hint || error;
  if (!hasMeta) {
    return wrapperClassName ? <div className={wrapperClassName}>{children}</div> : <>{children}</>;
  }
  return (
    <div className={['space-y-1.5', wrapperClassName].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text.body">
          {label}
          {required && (
            <span className="text-red-600 ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text.muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export const Input = forwardRef(function Input(
  { label = null, hint = null, error = null, required = false, className = '', wrapperClassName = '', id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  return (
    <Field id={inputId} label={label} hint={hint} error={error} required={required} wrapperClassName={wrapperClassName}>
      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-required={required ? 'true' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={[baseField, error ? errorClass : ringClass, className].filter(Boolean).join(' ')}
        {...rest}
      />
    </Field>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label = null, hint = null, error = null, required = false, rows = 4, className = '', id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  return (
    <Field id={inputId} label={label} hint={hint} error={error} required={required}>
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        required={required}
        aria-required={required ? 'true' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={[baseField, error ? errorClass : ringClass, 'resize-y', className].filter(Boolean).join(' ')}
        {...rest}
      />
    </Field>
  );
});

export const Select = forwardRef(function Select(
  { label = null, hint = null, error = null, required = false, className = '', id, children, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  return (
    <Field id={inputId} label={label} hint={hint} error={error} required={required}>
      <select
        ref={ref}
        id={inputId}
        required={required}
        aria-required={required ? 'true' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={[baseField, error ? errorClass : ringClass, 'pr-8', className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </select>
    </Field>
  );
});

export default Input;
